/**
 * TargetBelowHpPercentCondition - behavior tree condition checking target HP.
 */

import { CharacterStatsComponent } from '../../../../components/CharacterStatsComponent.js';
import { EngineClass } from '../../../../systems/ClassRegistry.js';
import { BehaviorStatus } from '../../BehaviorNode.js';
import { ConditionEvaluator, type ConditionEvaluatorOptions } from '../ConditionEvaluator.js';

import type { Actor } from '../../../../actors/Actor.js';
import type { Blackboard } from '../../blackboard/Blackboard.js';

export interface TargetBelowHpPercentConditionOptions extends ConditionEvaluatorOptions {
  percentage?: number;
  targetActorKey?: string;
}

@EngineClass('TargetBelowHpPercentCondition')
export class TargetBelowHpPercentCondition extends ConditionEvaluator {
  private percentage: number;
  private targetActorKey?: string;

  constructor(options: TargetBelowHpPercentConditionOptions = {}) {
    super({
      name: 'TargetBelowHpPercent',
      ...options
    });

    this.percentage = options.percentage ?? 25.0;
    this.targetActorKey = options.targetActorKey ?? 'PlayerActor';
  }

  protected override async onEvaluate(blackboard: Blackboard): Promise<BehaviorStatus> {
    // Get target actor
    let targetActor: Actor | null = null;

    if (this.targetActorKey) {
      targetActor = blackboard.getValue<Actor>(this.targetActorKey) ?? null;
    }

    if (!targetActor) {
      return BehaviorStatus.Failure;
    }

    // Get the target's stats component
    const statsComponent = targetActor.getComponent(CharacterStatsComponent);
    if (!statsComponent) {
      return BehaviorStatus.Failure;
    }

    // Calculate current HP percentage
    const currentHp = statsComponent.getCurrentHealth();
    const maxHp = statsComponent.getMaxHealth();
    const currentPercent = (currentHp / maxHp) * 100;

    // Check if HP is below threshold
    return currentPercent < this.percentage ? BehaviorStatus.Success : BehaviorStatus.Failure;
  }
}